import Canvas from '@/common/components/wavvest-planning/Canvas';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import {
  addUserMessage,
  resetCanvas,
  updateVisualization,
  updateConversationAdditionalContextJson,
} from '@/store/features/canvas/canvasSlice';
import canvasWebSocket from '@/services/websocket-service';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchInvestorsAllThunk } from '@/store/features/investors/investorsThunk';
import {
  selectInvestors,
  selectInvestorsLoadingState,
} from '@/store/features/investors/investorsSlice';
import {
  AdvisorRouteError,
  AdvisorRouteSkeleton,
} from '@/routes/components/route-loading-skeleton';
import { message } from 'antd';

const CanvasPage: FC = () => {
  const location = useLocation();
  const { state } = location;
  const dispatch = useAppDispatch();

  const [initStatus, setInitStatus] = useState<'initializing' | 'initialized' | 'failed'>(
    'initializing',
  );
  const navigate = useNavigate();

  const investors = useAppSelector(selectInvestors);
  const investorsLoadingState = useAppSelector(selectInvestorsLoadingState);

  const isChildRoute =
    location.pathname.includes('/planning/sandbox-mode') ||
    location.pathname.includes('/planning/guided-mode');

  // Fetch investors on mount if not already loaded.
  // This ensures investors are available for both guided and sandbox modes.
  useEffect(() => {
    if (investors.length === 0 && investorsLoadingState === 'idle') {
      dispatch(fetchInvestorsAllThunk());
    }
  }, [investors.length, investorsLoadingState, dispatch]);

  useEffect(() => {
    async function processData() {
      if (!isTemplateRouteState(state)) {
        return;
      }
      try {
        // create a new session to replace active session
        await canvasWebSocket.createNewSession();
        dispatch(resetCanvas());
        const result = processDataFromTemplates(state);
        if (state.template?.templateSourceCode) {
          dispatch(
            updateVisualization({
              sourceCode: state.template.templateSourceCode,
            }),
          );
          // Note: Don't spread existing additionalContextJson here.
          // resetCanvas() was called above to start fresh, so we only set the template.
          // Spreading would re-add old data (plaidData, etc.) after the reset.
          dispatch(
            updateConversationAdditionalContextJson({
              template: JSON.stringify({
                currentTemplate: state?.template?.templateSourceCode,
                userNotes: `Please refer to/use this template to generate the visualization.`,
              }),
            }),
          );
        }
        if (result.initialPrompt) {
          dispatch(addUserMessage(result.initialPrompt));
        }
      } catch (e) {
        message.error('Failed to process data from planning studio, please try again later');
        console.error(e);
      }
    }
    processData();
  }, [state, dispatch]);

  useEffect(() => {
    const initialize = async () => {
      try {
        if (!canvasWebSocket.getConnectionStatus()) {
          const connected = await canvasWebSocket.connect();
          if (!connected) {
            setInitStatus('failed');
            return;
          }
        }
        setInitStatus('initialized');
      } catch (error) {
        console.error(error);
        setInitStatus('failed');
      }
    };
    initialize();
  }, []);

  // Always default to guided-mode-v2 on base planning route
  useEffect(() => {
    if (location.pathname === '/home/planning') {
      navigate('/home/planning/guided-mode-v2', { replace: true, state: location.state ?? null });
      return;
    }
  }, [location.pathname, location.state, navigate]);

  if (initStatus === 'initializing') {
    return isChildRoute ? <Outlet /> : <AdvisorRouteSkeleton pathname={location.pathname} />;
  }

  if (initStatus === 'failed') {
    return isChildRoute ? (
      <Outlet />
    ) : (
      <AdvisorRouteError
        title="We could not start the planning workspace."
        description="Please reload the page and try opening the planner again."
      />
    );
  }

  return isChildRoute ? <Outlet /> : <Canvas />;
};

export default CanvasPage;

interface TemplateData {
  basePrompt?: string;
  template?: {
    templateSourceCode?: string;
  };
  useTemplate: true;
}

function isTemplateRouteState(data: unknown): data is TemplateData {
  return typeof data === 'object' && data !== null && (data as TemplateData).useTemplate === true;
}

function processDataFromTemplates(data: TemplateData) {
  const { basePrompt } = data;
  return {
    initialPrompt: basePrompt,
    initialFileReferences: undefined,
    additionalContext: undefined,
  };
}
